import { makeAutoObservable } from 'mobx'
import { getCountryNameByIso2 } from '../../../shared/utils/countryNameByIso2'
import { resolveStudentCountryIso2List } from '../constants/studentCountriesDisplay'
// import {
// 	fetchStudentLists,
// 	createStudentList,
// 	deleteStudentList as deleteStudentListAPI,
// } from '../../../api/users'

const CURRICULUM_GRADE_NUMBERS = [9, 10, 11, 12]

const DEFAULT_PANEL_FILTERS = {
	studentNationality: [], // ISO2: ['US', 'DE'] - used for country of application
	grade: [], // ['Grade 11', 'Grade 12']
	assignmentStatus: [],
	course: [], // renamed from stream
}

class StudentsStore {
	students = []
	view = 'table'
	searchQuery = ''
	selectedFilters = []
	activeGrade = 'all'
	selectedStudentIds = []
	sortKey = null
	sortDir = 'asc'
	// savedLists = [] // [{ id, name, studentIds, createdAt, updatedAt }]
	// listsLoadStatus = 'idle' // 'idle' | 'loading' | 'success' | 'error'
	// isLoadingLists = false
	
	// Students loading status
	studentsLoadStatus = 'idle' // 'idle' | 'loading' | 'success' | 'error'
	isLoadingStudents = false

	// Filters panel state
	panelFilters = { ...DEFAULT_PANEL_FILTERS }
	hasPanelFilters = false

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	setStudents(students) {
		this.students = students || []
	}

	setView(newView) {
		this.view = newView
	}

	setSearchQuery(query) {
		this.searchQuery = query || ''
	}

	toggleFilter(filter) {
		if (this.selectedFilters.includes(filter)) {
			this.selectedFilters = this.selectedFilters.filter(f => f !== filter)
		} else {
			this.selectedFilters.push(filter)
		}
	}

	clearFilters() {
		this.selectedFilters = []
	}

	setGrade(grade) {
		this.activeGrade = grade
	}

	toggleStudentSelection(id) {
		const sid = id == null ? '' : String(id)
		const has = this.selectedStudentIds.some(item => String(item) === sid)
		if (has) {
			this.selectedStudentIds = this.selectedStudentIds.filter(
				item => String(item) !== sid
			)
		} else {
			this.selectedStudentIds = [...this.selectedStudentIds, sid]
		}
	}

	clearSelection() {
		this.selectedStudentIds = []
	}

	selectAllForCurrentList(ids) {
		if (!ids || ids.length === 0) return

		if (this.selectedStudentIds.length === ids.length) {
			this.selectedStudentIds = []
		} else {
			this.selectedStudentIds = ids
		}
	}

	setSort(key, dir) {
		if (!key) return

		if (dir === 'asc' || dir === 'desc') {
			this.sortKey = key
			this.sortDir = dir
			return
		}

		if (this.sortKey === key) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
		} else {
			this.sortKey = key
			this.sortDir = 'asc'
		}
	}

	clearSort() {
		this.sortKey = null
		this.sortDir = 'asc'
	}

	applyPanelFilters(filters) {
		this.panelFilters = {
			...DEFAULT_PANEL_FILTERS,
			...(filters || {}),
		}

		const hasAny =
			(this.panelFilters.studentNationality?.length || 0) > 0 ||
			(this.panelFilters.grade?.length || 0) > 0 ||
			(this.panelFilters.assignmentStatus?.length || 0) > 0 ||
			(this.panelFilters.course?.length || 0) > 0

		this.hasPanelFilters = hasAny
	}

	clearPanelFilters() {
		this.panelFilters = { ...DEFAULT_PANEL_FILTERS }
		this.hasPanelFilters = false
	}

	get selectedCount() {
		return this.selectedStudentIds.length
	}

	get hasSelection() {
		return this.selectedStudentIds.length > 0
	}

	get consultantGrades() {
		const grades = new Set()

		for (const student of this.students) {
			const grade = Number(student?.grade)
			if (Number.isFinite(grade) && grade > 0) {
				grades.add(grade)
			}
		}

		return Array.from(grades).sort((a, b) => a - b)
	}

	get consultantCurriculumGrades() {
		return this.consultantGrades.filter(grade =>
			CURRICULUM_GRADE_NUMBERS.includes(grade),
		)
	}

	get firstConsultantCurriculumGradeId() {
		const first = this.consultantCurriculumGrades[0]
		return first != null ? `grade-${first}` : null
	}

	get selectedStudents() {
		return this.students.filter(s =>
			this.selectedStudentIds.some(
				idx => String(idx) === String(s.id)
			)
		)
	}

	get filteredStudents() {
		let filtered = [...this.students]

		// Search
		if (this.searchQuery) {
			const q = this.searchQuery.toLowerCase()
			filtered = filtered.filter(s =>
				String(s.name || '')
					.toLowerCase()
					.includes(q)
			)
		}

		// Grade & saved-list tabs
		if (this.activeGrade && this.activeGrade !== 'all' && this.activeGrade !== 'missing') {
			// Saved list tab: id format 'list:<listId>'
			// if (
			// 	typeof this.activeGrade === 'string' &&
			// 	this.activeGrade.startsWith('list:')
			// ) {
			// 	const listId = this.activeGrade.split(':', 1)[0] === 'list'
			// 		? this.activeGrade.substring('list:'.length)
			// 		: null

			// 	if (listId) {
			// 		const list = this.savedLists.find(l => l.id === listId)
			// 		if (list && Array.isArray(list.studentIds) && list.studentIds.length) {
			// 			const allowedIds = new Set(
			// 				list.studentIds.map(id => String(id).trim())
			// 			)
			// 			filtered = filtered.filter(s =>
			// 				allowedIds.has(String(s.id).trim())
			// 			)
			// 		} else {
			// 			// No students in this list -> empty result
			// 			filtered = []
			// 		}
			// 	}
			// } else {
				// Numeric grade tab: id is "grade-10", "grade-11", etc.
				const gradeNum =
					typeof this.activeGrade === 'string' && this.activeGrade.startsWith('grade-')
						? Number(this.activeGrade.replace('grade-', ''))
						: Number(this.activeGrade)
				if (!Number.isNaN(gradeNum)) {
					filtered = filtered.filter(s => Number(s.grade) === gradeNum)
				}
			// }
		}

		// Status filters (existing logic)
		if (this.selectedFilters.length > 0) {
			filtered = filtered.filter(s => this.selectedFilters.includes(s.status))
		}

		// Right panel filters (applied by Search button)
		if (this.hasPanelFilters) {
			const pf = this.panelFilters

			// Country of application -> any of student's target/display countries
			if (pf.studentNationality?.length) {
				filtered = filtered.filter(s => {
					const codes = resolveStudentCountryIso2List(s)
					return codes.some(code => pf.studentNationality.includes(code))
				})
			}

			// Grade filter -> student.grade is number (11/12), filter values are 'Grade 11'
			if (pf.grade?.length) {
				const allowed = pf.grade
					.map(v => String(v).replace('Grade', '').trim())
					.map(v => Number(v))
					.filter(n => !Number.isNaN(n))

				if (allowed.length) {
					filtered = filtered.filter(s => allowed.includes(Number(s.grade)))
				}
			}

			// The following depend on real student fields (kept safe with guards)
			if (pf.assignmentStatus?.length) {
				filtered = filtered.filter(
					s =>
						s.assignmentStatus &&
						pf.assignmentStatus.includes(s.assignmentStatus)
				)
			}

			if (pf.course?.length) {
				filtered = filtered.filter(
					s => s.course && pf.course.includes(s.course)
				)
			}
		}

		// Sorting (supports all column keys: student, grade, country, course, counselor, deadline, etc.)
		if (this.sortKey) {
			const dir = this.sortDir === 'asc' ? 1 : -1
			const key = this.sortKey

			const keyToField = {
				student: 'name',
				grade: 'grade',
				country: 'country',
				tags: 'tag',
				course: 'course',
				counselor: 'counselor',
				nearest_deadline: 'nearest_deadline',
				deadline: 'nearest_deadline',
				joined_date: 'joined_date',
				email: 'email',
				section: 'section',
			}
			const field = keyToField[key] ?? key

			const getValue = s => {
				const raw = s[field] ?? s[key]
				if (key === 'country') {
					const codes = resolveStudentCountryIso2List(s)
					if (!codes.length) return ''
					return codes.map(code => getCountryNameByIso2(code) || code).join(', ')
				}
				if (key === 'tags') return s.tag || s.status
				return raw
			}

			const parseDate = v => {
				if (v == null) return null
				if (typeof v === 'number' && !Number.isNaN(v)) return v
				const d = new Date(v)
				return Number.isNaN(d.getTime()) ? null : d.getTime()
			}

			filtered.sort((a, b) => {
				const av = getValue(a)
				const bv = getValue(b)

				if (av == null && bv == null) return 0
				if (av == null) return 1
				if (bv == null) return -1

				const dateKeys = ['nearest_deadline', 'deadline', 'joined_date', 'due_date']
				const aTime = dateKeys.includes(key) ? parseDate(av) : null
				const bTime = dateKeys.includes(key) ? parseDate(bv) : null
				if (aTime != null && bTime != null) {
					return (aTime - bTime) * dir
				}

				if (typeof av === 'number' && typeof bv === 'number') {
					return (av - bv) * dir
				}

				return (
					String(av).localeCompare(String(bv), undefined, {
						sensitivity: 'base',
					}) * dir
				)
			})
		}

		return filtered
	}

	// async loadStudentLists() {
	// 	if (this.listsLoadStatus !== 'idle' || this.isLoadingLists) {
	// 		return
	// 	}

	// 	try {
	// 		this.isLoadingLists = true
	// 		this.listsLoadStatus = 'loading'

	// 		const response = await fetchStudentLists()
	// 		const apiLists = response.lists || []

	// 		// Merge API lists with existing local lists (avoid duplicates by ID)
	// 		const existingIds = new Set(this.savedLists.map(l => l.id))
	// 		apiLists.filter(l => !existingIds.has(l.id))

	// 		// Normalize API list format
	// 		const normalizedApiLists = apiLists.map(list => ({
	// 			id: list.id,
	// 			name: list.name,
	// 			studentIds: list.studentIds || [],
	// 			createdAt:
	// 				typeof list.createdAt === 'string'
	// 					? new Date(list.createdAt).getTime()
	// 					: list.createdAt || Date.now(),
	// 			updatedAt:
	// 				typeof list.updatedAt === 'string'
	// 					? new Date(list.updatedAt).getTime()
	// 					: list.updatedAt || Date.now(),
	// 		}))

	// 		// Merge: API lists first (most recent), then local-only lists
	// 		const localOnlyLists = this.savedLists.filter(
	// 			l => !normalizedApiLists.some(api => api.id === l.id)
	// 		)

	// 		this.savedLists = [...normalizedApiLists, ...localOnlyLists]
	// 		this.listsLoadStatus = 'success'
	// 	} catch (error) {
	// 		console.error('Error loading student lists:', error)
	// 		this.listsLoadStatus = 'error'
	// 		// Don't clear existing lists on error
	// 	} finally {
	// 		this.isLoadingLists = false
	// 	}
	// }

	// async saveCurrentSelectionAsList(name) {
	// 	const trimmed = String(name || '').trim()
	// 	if (!trimmed) return

	// 	const ids = [...this.selectedStudentIds]
	// 	if (ids.length === 0) return

	// 	const now = Date.now()

	// 	// Check for duplicate name in current lists
	// 	const exists = this.savedLists?.some(
	// 		l => String(l.name || '').toLowerCase() === trimmed.toLowerCase()
	// 	)

	// 	const finalName = exists ? `${trimmed} (${now})` : trimmed

	// 	// Generate temporary unique ID for optimistic update
	// 	const tempId = generateUniqueId()

	// 	// Create list object for local state (temporary ID)
	// 	const newList = {
	// 		id: tempId,
	// 		name: finalName,
	// 		studentIds: ids,
	// 		createdAt: now,
	// 		updatedAt: now,
	// 	}

	// 	// Add to local state immediately (optimistic update)
	// 	this.savedLists = [newList, ...this.savedLists]

	// 	// Save to API in background and update with real ID from API
	// 	try {
	// 		const apiResponse = await createStudentList(finalName, ids)
	// 		// Update with API response ID (API generates UUID)
	// 		if (apiResponse.id && apiResponse.id !== tempId) {
	// 			const index = this.savedLists.findIndex(l => l.id === tempId)
	// 			if (index !== -1) {
	// 				this.savedLists[index].id = apiResponse.id
	// 			}
	// 		}
	// 	} catch (error) {
	// 		console.error('Error saving list to API:', error)
	// 		// Keep local list even if API fails (with temp ID)
	// 	}
	// }

	// async deleteSavedList(listId) {
	// 	// Remove from local state immediately (optimistic update)
	// 	const listToDelete = this.savedLists.find(l => l.id === listId)
	// 	if (!listToDelete) return

	// 	this.savedLists = this.savedLists.filter(l => l.id !== listId)

	// 	// Delete from API in background
	// 	try {
	// 		await deleteStudentListAPI(listId)
	// 	} catch (error) {
	// 		console.error('Error deleting list from API:', error)
	// 		// Restore list if API deletion failed
	// 		if (listToDelete) {
	// 			this.savedLists = [listToDelete, ...this.savedLists]
	// 		}
	// 	}
	// }

	// applySavedList(listId) {
	// 	const list = this.savedLists.find(l => l.id === listId)
	// 	if (!list) return
	// 	this.selectedStudentIds = [...list.studentIds]
	// }

	getStudentById(id) {
		if (id == null) return null
		// Normalize both IDs for comparison
		const normalizedId = String(id).trim()
		return this.students.find(s => String(s.id).trim() === normalizedId) || null
	}

	getIndexById(id, list = this.filteredStudents) {
		if (!list?.length) return -1
		// Normalize both IDs for comparison
		const normalizedId = String(id).trim()
		return list.findIndex(s => String(s.id).trim() === normalizedId)
	}

	getPrevNextIds(id, list = this.filteredStudents) {
		const idx = this.getIndexById(id, list)
		if (idx === -1)
			return { prevId: null, nextId: null, index: -1, total: list.length || 0 }

		const prev = idx > 0 ? list[idx - 1] : null
		const next = idx < list.length - 1 ? list[idx + 1] : null

		return {
			prevId: prev ? prev.id : null,
			nextId: next ? next.id : null,
			index: idx,
			total: list.length,
		}
	}
}

export const studentsStore = new StudentsStore()
